"use client";

import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Bell, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

const NotificationBell: React.FC = () => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const isClient = profile?.role === "client";
  const listRoute = isClient ? "/client-portal/notifications" : "/dashboard/notifications";

  const { data: unreadCount = 0, refetch: refetchUnread } = useQuery({
    queryKey: ["notifications-unread-count", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", profile.id)
        .eq("read", false);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!profile?.id,
  });

  const { data: latest = [], refetch: refetchLatest } = useQuery({
    queryKey: ["notifications-latest", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { data, error } = await supabase
        .from("notifications")
        .select("id, title, message, created_at, read, type")
        .eq("user_id", profile.id)
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
    enabled: !!profile?.id,
  });

  React.useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel("notifications-bell")
      .on(
        "postgres_changes",
        { schema: "public", table: "notifications", event: "*", filter: `user_id=eq.${profile.id}` },
        () => {
          refetchUnread();
          refetchLatest();
          queryClient.invalidateQueries({ queryKey: ["notifications"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, queryClient, refetchLatest, refetchUnread]);

  const markAllAsRead = async () => {
    if (!profile?.id) return;
    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", profile.id)
      .eq("read", false);
    if (!error) {
      refetchUnread();
      refetchLatest();
    }
  };

  if (!profile) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1">
              <Badge variant="destructive" className="px-1 py-0 text-[10px]">
                {unreadCount}
              </Badge>
            </span>
          )}
          <span className="sr-only">Open notifications</span>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="end"
        sideOffset={8}
        className={cn(
          "z-[9999] w-80 rounded-md border bg-popover p-2 text-popover-foreground shadow-md",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[side=bottom]:slide-in-from-top-2"
        )}
      >
        <div className="flex items-center justify-between px-2 py-1.5">
          <div className="text-sm font-medium">Notifications</div>
          <Button variant="outline" size="sm" onClick={markAllAsRead} disabled={unreadCount === 0}>
            <Check className="h-4 w-4 mr-1" />
            Mark all read
          </Button>
        </div>
        <div className="max-h-80 overflow-auto">
          {latest.length === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
              You’re all caught up.
            </div>
          ) : (
            <ul className="space-y-1">
              {latest.map((n: any) => (
                <li key={n.id}>
                  <DropdownMenu.Item
                    onSelect={(e) => {
                      e.preventDefault();
                      navigate(listRoute);
                    }}
                    className={cn(
                      "rounded-sm px-2 py-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground cursor-pointer",
                      !n.read ? "bg-primary/5 border border-primary/20" : ""
                    )}
                  >
                    <div className="font-medium line-clamp-1">{n.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                  </DropdownMenu.Item>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-2 pt-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between"
            onClick={() => navigate(listRoute)}
          >
            View all
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default NotificationBell;